/**
 * AgreedValueDialog — Feature 5: Agreed Value Billing
 *
 * Shown when the PM clicks "Set agreed value for selected lines" in the
 * Bulk Adjustments dropdown inside the Invoice Generation Wizard (Step 1).
 *
 * The dialog lets the PM enter an agreed total for the selected lines. The
 * difference between the actual total and the agreed value is calculated as
 * either a write-off (agreed < actual) or a write-up / uplift (agreed > actual).
 *
 * Write-off / write-up reasons and the requireWriteDownReason / requireWriteUpReason
 * flags are fetched from the settings API (GET /api/settings/billing and
 * GET /api/settings/write-off-reasons), so the list matches what is configured
 * in Project Settings → Billing.
 *
 * The actual WIP adjustment entries are created on the backend when the
 * invoice is posted to Financials (PostToFinancialsAsync), not at dialog
 * confirm time.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { AlertCircle, ArrowLeftRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { fetchBillingSettings } from "@/lib/data-service";
import {
  isDemoDataMode,
  loadWipAdjustmentReasons,
  type WipAdjustmentReason,
} from "@/lib/wip-adjustment-reasons";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AgreedValueLineItem {
  id: string;
  description: string;
  amount: number;
}

export interface AgreedValueAllocation {
  lineId: string;
  amount: number;
}

export type AgreedValueDirection = "write_up" | "write_down" | "none";

export interface AgreedValueResult {
  /** IDs of the affected line items */
  lineIds: string[];
  /** The agreed total the PM entered */
  agreedTotal: number;
  /**
   * Signed adjustment amount: actualTotal − agreedTotal.
   * Positive  → write-off (agreed is less than actual).
   * Negative  → write-up  (agreed is more than actual).
   * Zero      → no adjustment.
   */
  writeOffAmount: number;
  /** Direction of the adjustment */
  direction: AgreedValueDirection;
  /** Reason code for the adjustment (write-off or write-up). Neutral alias. */
  adjustmentReason?: string;
  /** @deprecated Use adjustmentReason. Kept for backward compatibility. */
  writeOffReason?: string;
  /** Scaling factor to apply to each line item's amount (agreedTotal / actualTotal) */
  scaleFactor: number;
  /** Penny-exact amounts allocated to each affected line */
  allocations: AgreedValueAllocation[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount);
}

export function allocateAgreedValue(
  lines: { id: string; amount: number }[],
  agreedTotal: number
): AgreedValueAllocation[] {
  const targetPence = Math.round(agreedTotal * 100);
  const linePence = lines.map((line) => Math.round(line.amount * 100));
  const totalPence = linePence.reduce((sum, amount) => sum + amount, 0);

  if (totalPence === 0) {
    return lines.map((line) => ({ lineId: line.id, amount: 0 }));
  }

  const allocations = linePence.map((amount, index) => {
    const exact = (amount * targetPence) / totalPence;
    const base = Math.floor(exact);
    return {
      lineId: lines[index].id,
      amount: base,
      remainder: exact - base,
      linePence: amount,
      index,
    };
  });
  const remainderPence = targetPence - allocations.reduce((sum, allocation) => sum + allocation.amount, 0);

  allocations
    .slice()
    .sort((left, right) =>
      right.remainder - left.remainder ||
      right.linePence - left.linePence ||
      left.index - right.index
    )
    .slice(0, remainderPence)
    .forEach((allocation) => {
      allocation.amount += 1;
    });

  return allocations.map(({ lineId, amount }) => ({
    lineId,
    amount: amount / 100,
  }));
}

// These keys must match the constants in data-service.ts (eb_requireWriteDownReason /
// eb_requireWriteUpReason) so the settings saved by WriteOffReasonsSettings are
// read correctly here when the API is unavailable.
const LS_REQUIRE_DOWN = "eb_requireWriteDownReason";
const LS_REQUIRE_UP   = "eb_requireWriteUpReason";

function loadRequireReasonFlagsFallback(): {
  requireWriteDownReason: boolean;
  requireWriteUpReason: boolean;
} {
  try {
    return {
      requireWriteDownReason: localStorage.getItem(LS_REQUIRE_DOWN) === "true",
      requireWriteUpReason:   localStorage.getItem(LS_REQUIRE_UP)   === "true",
    };
  } catch {
    return { requireWriteDownReason: false, requireWriteUpReason: false };
  }
}

interface WriteOffSettings {
  reasons: WipAdjustmentReason[];
  reasonsFailed: boolean;
  requireWriteDownReason: boolean;
  requireWriteUpReason: boolean;
}

async function fetchWriteOffSettings(): Promise<WriteOffSettings> {
  let reasons: WipAdjustmentReason[] = [];
  let reasonsFailed = false;
  try {
    reasons = await loadWipAdjustmentReasons();
  } catch (error) {
    console.error("[AgreedValue] Failed to load WIP adjustment reasons", error);
    reasonsFailed = true;
  }

  if (isDemoDataMode()) {
    return { reasons, reasonsFailed, ...loadRequireReasonFlagsFallback() };
  }

  try {
    const billingSettings = await fetchBillingSettings();
    return {
      reasons,
      reasonsFailed,
      requireWriteDownReason: billingSettings.requireWriteDownReason,
      requireWriteUpReason: billingSettings.requireWriteUpReason,
    };
  } catch {
    return { reasons, reasonsFailed, ...loadRequireReasonFlagsFallback() };
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

interface AgreedValueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedLines: AgreedValueLineItem[];
  onConfirm: (result: AgreedValueResult) => void;
}

export function AgreedValueDialog({
  open,
  onOpenChange,
  selectedLines,
  onConfirm,
}: AgreedValueDialogProps) {
  const [agreedValueRaw, setAgreedValueRaw] = useState("");
  const [agreedValueDisplay, setAgreedValueDisplay] = useState("");
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const [{ reasons, reasonsFailed, requireWriteDownReason, requireWriteUpReason }, setSettings] =
    useState<WriteOffSettings>({
      reasons: [],
      reasonsFailed: false,
      requireWriteDownReason: false,
      requireWriteUpReason: false,
    });
  const reasonChosenByUser = useRef(false);

  const loadSettings = useCallback(async () => {
    const settings = await fetchWriteOffSettings();
    setSettings(settings);
  }, []);

  useEffect(() => {
    if (open) {
      reasonChosenByUser.current = false;
      loadSettings();
      setAgreedValueRaw("");
      setAgreedValueDisplay("");
      setAdjustmentReason("");
    }
  }, [open, loadSettings]);

  // Pre-select the reason configured as default in Project Settings > Billing,
  // unless the user has already picked one for this open session.
  useEffect(() => {
    if (!open || reasonChosenByUser.current) return;
    const defaultReason = reasons.find((r) => r.isDefault);
    if (defaultReason) {
      setAdjustmentReason(defaultReason.code);
    }
  }, [open, reasons]);

  const handleReasonChange = useCallback((value: string) => {
    reasonChosenByUser.current = true;
    setAdjustmentReason(value);
  }, []);

  const actualTotal = useMemo(
    () => selectedLines.reduce((sum, l) => sum + l.amount, 0),
    [selectedLines]
  );

  const agreedNumeric = useMemo(() => {
    const raw = parseFloat(agreedValueRaw);
    return isNaN(raw) ? null : raw;
  }, [agreedValueRaw]);

  // Positive → write-off, negative → write-up, zero → no adjustment
  const writeOffAmount = useMemo(() => {
    if (agreedNumeric === null) return null;
    return actualTotal - agreedNumeric;
  }, [actualTotal, agreedNumeric]);

  const direction = useMemo<AgreedValueDirection>(() => {
    if (writeOffAmount === null) return "none";
    if (writeOffAmount > 0) return "write_down";
    if (writeOffAmount < 0) return "write_up";
    return "none";
  }, [writeOffAmount]);

  const hasError = useMemo(() => {
    if (agreedNumeric === null) return false;
    if (agreedNumeric <= 0) return true;
    if (actualTotal === 0) return true;
    return false;
  }, [actualTotal, agreedNumeric]);

  const errorMessage = useMemo(() => {
    if (agreedNumeric === null) return null;
    if (agreedNumeric <= 0) return "Agreed value must be greater than zero.";
    if (actualTotal === 0) return "The selected lines have no value to adjust, so an agreed value cannot be applied.";
    return null;
  }, [actualTotal, agreedNumeric]);

  // Whether the reason field is visible and whether it blocks confirm
  const showReason = useMemo(() => {
    if (writeOffAmount === null || hasError || writeOffAmount === 0) return false;
    if (direction === "write_down") return requireWriteDownReason;
    if (direction === "write_up") return requireWriteUpReason;
    return false;
  }, [writeOffAmount, hasError, direction, requireWriteDownReason, requireWriteUpReason]);

  const reasonRequired = useMemo(() => {
    if (!showReason) return false;
    if (direction === "write_down") return requireWriteDownReason;
    if (direction === "write_up") return requireWriteUpReason;
    return false;
  }, [showReason, direction, requireWriteDownReason, requireWriteUpReason]);

  const canConfirm = useMemo(() => {
    if (agreedNumeric === null || hasError) return false;
    if (showReason && reasonRequired && !adjustmentReason) return false;
    return true;
  }, [agreedNumeric, hasError, showReason, reasonRequired, adjustmentReason]);

  // Format on blur
  const handleBlur = () => {
    if (agreedNumeric !== null && !hasError) {
      setAgreedValueDisplay(
        new Intl.NumberFormat("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(agreedNumeric)
      );
    }
  };

  // Strip formatting on focus
  const handleFocus = () => {
    setAgreedValueDisplay(agreedValueRaw);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits and one decimal point
    const cleaned = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
    setAgreedValueRaw(cleaned);
    setAgreedValueDisplay(cleaned);
  };

  const handleConfirm = () => {
    if (!canConfirm || agreedNumeric === null || writeOffAmount === null || actualTotal <= 0) return;

    const scaleFactor = agreedNumeric / actualTotal;
    const allocations = allocateAgreedValue(selectedLines, agreedNumeric);

    const result: AgreedValueResult = {
      lineIds: selectedLines.map((l) => l.id),
      agreedTotal: agreedNumeric,
      writeOffAmount,
      direction,
      adjustmentReason: adjustmentReason || undefined,
      // Backward-compatible alias
      writeOffReason: adjustmentReason || undefined,
      scaleFactor,
      allocations,
    };

    // WIP adjustment entries are created by the backend in PostToFinancialsAsync
    // when the invoice is successfully posted to Access Financials.
    onConfirm(result);
    onOpenChange(false);
  };

  const upliftAmount = writeOffAmount !== null ? Math.abs(writeOffAmount) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-full overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5 text-muted-foreground" />
            Set Agreed Value
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2 min-w-0">
          {/* Selected lines summary */}
          <div className="rounded-lg border bg-muted/30 divide-y overflow-hidden">
            {selectedLines.map((line) => (
              <div
                key={line.id}
                className="flex items-center gap-3 px-3 py-2 text-sm min-w-0"
              >
                <span className="text-muted-foreground flex-1 min-w-0 truncate">
                  {line.description}
                </span>
                <span className="font-medium tabular-nums shrink-0 whitespace-nowrap">
                  {formatCurrency(line.amount)}
                </span>
              </div>
            ))}
          </div>

          {/* Actual total */}
          <div className="flex items-center justify-between gap-4 px-1">
            <span className="text-sm text-muted-foreground shrink-0">Actual total</span>
            <span className="text-sm font-semibold tabular-nums shrink-0">
              {formatCurrency(actualTotal)}
            </span>
          </div>

          <Separator />

          {/* Agreed value input */}
          <div className="space-y-2">
            <Label htmlFor="agreed-value">Agreed value</Label>
            <div
              className={cn(
                "flex items-center rounded-md border bg-background px-3 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
                hasError && "border-destructive focus-within:ring-destructive"
              )}
            >
              <span className="text-sm text-muted-foreground mr-2 select-none">£</span>
              <input
                id="agreed-value"
                type="text"
                inputMode="decimal"
                className="flex-1 py-2 text-sm bg-transparent focus:outline-none"
                placeholder="0.00"
                value={agreedValueDisplay}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>
            {errorMessage && (
              <p className="flex items-center gap-1.5 text-xs text-destructive">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                {errorMessage}
              </p>
            )}
          </div>

          {/* Write-off preview (agreed < actual) */}
          {writeOffAmount !== null && !hasError && writeOffAmount > 0 && (
            <div className="rounded-lg border border-warning-200 bg-warning-50 dark:border-warning-800 dark:bg-warning-950/30 px-4 py-3 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-warning-800 dark:text-warning-300">
                  Write-off
                </span>
                <span className="text-sm font-semibold tabular-nums text-warning-800 dark:text-warning-300">
                  {formatCurrency(writeOffAmount)}
                </span>
              </div>
              <p className="text-xs text-warning-700 dark:text-warning-400">
                The difference between the actual and agreed value will be
                recorded as a write-off against this invoice.
              </p>
            </div>
          )}

          {/* Write-up preview (agreed > actual) */}
          {writeOffAmount !== null && !hasError && writeOffAmount < 0 && (
            <div className="rounded-lg border border-success-200 bg-success-50 dark:border-success-800 dark:bg-success-950/30 px-4 py-3 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-success-800 dark:text-success-300">
                  Write-up
                </span>
                <span className="text-sm font-semibold tabular-nums text-success-800 dark:text-success-300">
                  {formatCurrency(upliftAmount)}
                </span>
              </div>
              <p className="text-xs text-success-700 dark:text-success-400">
                The difference between the agreed and actual value will be
                recorded as a write-up against this invoice.
              </p>
            </div>
          )}

          {/* No adjustment */}
          {writeOffAmount !== null && !hasError && writeOffAmount === 0 && (
            <p className="text-xs text-muted-foreground text-center">
              The agreed value matches the actual total — no write-off will be recorded.
            </p>
          )}

          {/* Reason field — shown for both write-offs and write-ups when the
              corresponding "require reason" flag is enabled in settings */}
          {showReason && (
            <div className="space-y-2">
              <Label htmlFor="adjustment-reason">
                {direction === "write_down" ? "Write-off reason" : "Write-up reason"}
                {reasonRequired && (
                  <span className="text-destructive ml-1">*</span>
                )}
              </Label>
              <Select value={adjustmentReason} onValueChange={handleReasonChange}>
                <SelectTrigger id="adjustment-reason">
                  <SelectValue placeholder="Select a reason…" />
                </SelectTrigger>
                <SelectContent>
                  {reasons.map((r) => (
                    <SelectItem key={r.code} value={r.code}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {reasonsFailed && (
                <p className="text-xs text-destructive">
                  Could not load adjustment reasons. Close the dialog and try again.
                </p>
              )}
              {!reasonsFailed && reasonRequired && !adjustmentReason && (
                <p className="text-xs text-muted-foreground">
                  A reason is required by your organisation's settings.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm}>
            Apply Agreed Value
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
